import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AdjustmentModal from "./adjustment-modal";
import { splitCamelCaseToWords } from "../../../../../utils/Formatter";

interface RushFee {
  id: string;
  type: string;
  label: string;
  clientFee: number;
  inspectorFee: number;
}

interface FeeRow {
  id: string;
  date: string;
  type: string;
  adjustment: string;
  amount: number;
  note: string;
  category: string;
}

type Props = {
  data: RushFee[];
  formik: any;
  formDetails: any;
  adjustmentTypes: any;
};

const Fees = (props: Props) => {
  const { data, formik, formDetails, adjustmentTypes } = props;
  const [clientFees, setClientFees] = useState<FeeRow[]>([]);
  const [inspectorFees, setInspectorFees] = useState<FeeRow[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [modalType, setModalType] = useState<"client" | "inspector">("client");
  const [editData, setEditData] = useState<FeeRow | undefined>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteData, setDeleteData] = useState<FeeRow | undefined>();
  const previousRushFeeLabelRef = useRef<string | undefined>(undefined);
  const toAmount = (value: any) => parseFloat(value as any) || 0;
  const keepFormRowFirst = (fees: FeeRow[]) => {
    const formRow = fees.find((fee) => fee.id === "FormRow");
    if (!formRow) return fees;
    return [formRow, ...fees.filter((fee) => fee.id !== "FormRow")];
  };

  const getAdjustmentDisplayName = (adjustmentType: string) => {
    if (!adjustmentType) return adjustmentType;

    const match = adjustmentTypes?.find(
      (a: { adjustmentType: string; displayName: string }) =>
        a.adjustmentType === adjustmentType
    );
    return match ? match.displayName : splitCamelCaseToWords(adjustmentType);
  };

  useEffect(() => {
    if (formik.values.fees && formik.values.fees.length > 0) {
      const clientFeesFromFormik = formik.values.fees
        .filter((fee: any) => fee.category === "ClientFee")
        .map((fee: any) => {
          // Use stable ID: FormRow for billing, or generate unique ID for adjustments
          let stableId = fee.id;
          if (!stableId) {
            if (
              fee.type === "Billing" &&
              (!fee.adjustmentType || fee.adjustmentType === null)
            ) {
              stableId = "FormRow";
            } else {
              stableId = `client-${Date.now()}-${Math.random()}`;
            }
          }

          return {
            id: stableId,
            date: fee.date || new Date().toISOString().split("T")[0],
            type: fee.type || "Billing",
            adjustment: fee.adjustmentType || "n/a",
            amount: fee.amount || 0,
            note: fee.note || "",
            category: "ClientFee",
          };
        });

      const inspectorFeesFromFormik = formik.values.fees
        .filter((fee: any) => fee.category === "InspectorFee")
        .map((fee: any) => {
          // Use stable ID: FormRow for cost, or generate unique ID for adjustments
          let stableId = fee.id;
          if (!stableId) {
            if (
              fee.type === "Cost" &&
              (!fee.adjustmentType || fee.adjustmentType === null)
            ) {
              stableId = "FormRow";
            } else {
              stableId = `inspector-${Date.now()}-${Math.random()}`;
            }
          }

          return {
            id: stableId,
            date: fee.date || new Date().toISOString().split("T")[0],
            type: fee.type || "Cost",
            adjustment: fee.adjustmentType || "n/a",
            amount: fee.amount || 0,
            note: fee.note || "",
            category: "InspectorFee",
          };
        });

      setClientFees(clientFeesFromFormik);
      setInspectorFees(inspectorFeesFromFormik);
    } else {
      setClientFees([]);
      setInspectorFees([]);
    }
  }, [formik.values.fees]);

  useEffect(() => {
    if (
      formDetails?.clientFee !== undefined &&
      formDetails?.inspectorFee !== undefined &&
      formDetails.clientFee !== null &&
      formDetails.inspectorFee !== null
    ) {
      const formRowId = "FormRow";

      setClientFees((prevClientFees) => {
        const existingFormRowIndex = prevClientFees.findIndex(
          (f) => f.id === formRowId
        );

        if (existingFormRowIndex >= 0) {
          const updatedFees = [...prevClientFees];
          updatedFees[existingFormRowIndex] = {
            ...updatedFees[existingFormRowIndex],
            amount: formDetails.clientFee,
          };
          return updatedFees;
        } else {
          return [
            {
              id: formRowId,
              date: new Date().toISOString().split("T")[0],
              type: "Billing",
              adjustment: "n/a",
              amount: formDetails.clientFee,
              note: "",
              category: "ClientFee",
            },
            ...prevClientFees,
          ];
        }
      });

      setInspectorFees((prevInspectorFees) => {
        const existingFormRowIndex = prevInspectorFees.findIndex(
          (f) => f.id === formRowId
        );

        if (existingFormRowIndex >= 0) {
          const updatedFees = [...prevInspectorFees];
          updatedFees[existingFormRowIndex] = {
            ...updatedFees[existingFormRowIndex],
            amount: formDetails.inspectorFee,
          };
          return updatedFees;
        } else {
          return [
            {
              id: formRowId,
              date: new Date().toISOString().split("T")[0],
              type: "Cost",
              adjustment: "n/a",
              amount: formDetails.inspectorFee,
              note: "",
              category: "InspectorFee",
            },
            ...prevInspectorFees,
          ];
        }
      });
    }
  }, [formDetails?.clientFee, formDetails?.inspectorFee]);

  useEffect(() => {
    const selectedRushFeeLabel = formik.values.rushFeeLabel || "";
    const previousRushFeeLabel = previousRushFeeLabelRef.current;

    if (previousRushFeeLabel === undefined) {
      previousRushFeeLabelRef.current = selectedRushFeeLabel;
      return;
    }

    if (selectedRushFeeLabel === previousRushFeeLabel) return;

    if (selectedRushFeeLabel && data && data.length > 0) {
      const selectedRushFee = data.find(
        (rushFee: RushFee) => rushFee.label === selectedRushFeeLabel
      );

      if (selectedRushFee) {
        setClientFees((prevClientFees) => {
          const filteredFees = prevClientFees.filter(
            (f) => f.adjustment !== "C_PR_RushFee"
          );
          const newRushFee = {
            id: `C_PR_RushFee-${Date.now()}`,
            date: new Date().toISOString().split("T")[0],
            type: "PreBillingAdjustment",
            adjustment: "C_PR_RushFee",
            amount: selectedRushFee.clientFee,
            note: "",
            category: "ClientFee",
          };
          return [...filteredFees, newRushFee];
        });

        setInspectorFees((prevInspectorFees) => {
          const filteredFees = prevInspectorFees.filter(
            (f) => f.adjustment !== "I_PR_RushFee"
          );
          const newRushFee = {
            id: `I_PR_RushFee-${Date.now()}`,
            date: new Date().toISOString().split("T")[0],
            type: "PreBillingAdjustment",
            adjustment: "I_PR_RushFee",
            amount: selectedRushFee.inspectorFee,
            note: "",
            category: "InspectorFee",
          };
          return [...filteredFees, newRushFee];
        });
      }
    } else {
      setClientFees((prevClientFees) =>
        prevClientFees.filter((f) => f.adjustment !== "C_PR_RushFee")
      );
      setInspectorFees((prevInspectorFees) =>
        prevInspectorFees.filter((f) => f.adjustment !== "I_PR_RushFee")
      );
    }

    previousRushFeeLabelRef.current = selectedRushFeeLabel;
  }, [formik.values.rushFeeLabel, data]);

  useEffect(() => {
    const allFees = [
      ...clientFees.map((fee) => ({
        id: fee.id,
        category: fee.category,
        type: fee.type,
        adjustmentType: fee.adjustment === "n/a" ? null : fee.adjustment,
        amount: fee.amount,
        note: fee.note,
        date: fee.date,
      })),
      ...inspectorFees.map((fee) => ({
        id: fee.id,
        category: fee.category,
        type: fee.type,
        adjustmentType: fee.adjustment === "n/a" ? null : fee.adjustment,
        amount: fee.amount,
        note: fee.note,
        date: fee.date,
      })),
    ];

    const currentFees = formik.values.fees || [];
    const feesChanged = JSON.stringify(allFees) !== JSON.stringify(currentFees);

    if (feesChanged && allFees.length > 0) {
      formik.setFieldValue("fees", allFees);
    }
  }, [clientFees, inspectorFees]); // eslint-disable-line

  const clientSubtotal = clientFees.reduce(
    (total, fee) => total + toAmount(fee.amount),
    0
  );
  const inspectorSubtotal = inspectorFees.reduce(
    (total, fee) => total + toAmount(fee.amount),
    0
  );
  const orderedClientFees = keepFormRowFirst(clientFees);
  const orderedInspectorFees = keepFormRowFirst(inspectorFees);

  const openAddModal = (type: "client" | "inspector") => {
    setModalType(type);
    setModalMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const openEditModal = (type: "client" | "inspector", data: FeeRow) => {
    setModalType(type);
    setModalMode("edit");
    setEditData(data);
    setIsModalOpen(true);
  };

  const handleSaveAdjustment = (data: FeeRow) => {
    if (modalType === "client") {
      if (modalMode === "edit") {
        setClientFees((prev) =>
          prev.map((fee) => (fee.id === data.id ? data : fee))
        );
      } else {
        const newData = {
          ...data,
          id: `client-adjustment-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        };
        setClientFees((prev) => [...prev, newData]);
      }
    } else {
      if (modalMode === "edit") {
        setInspectorFees((prev) =>
          prev.map((fee) => (fee.id === data.id ? data : fee))
        );
      } else {
        const newData = {
          ...data,
          id: `inspector-adjustment-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        };
        setInspectorFees((prev) => [...prev, newData]);
      }
    }
  };

  const handleDeleteAdjustment = (id: string) => {
    if (modalType === "client") {
      setClientFees((prev) => prev.filter((fee) => fee.id !== id));
    } else {
      setInspectorFees((prev) => prev.filter((fee) => fee.id !== id));
    }
  };

  const handleDeleteClientRow = (id: string) => {
    const feeToDelete = clientFees.find((fee) => fee.id === id);
    if (feeToDelete) {
      setDeleteData(feeToDelete);
      setModalType("client");
      setIsDeleteModalOpen(true);
    }
  };

  const handleDeleteInspectorRow = (id: string) => {
    const feeToDelete = inspectorFees.find((fee) => fee.id === id);
    if (feeToDelete) {
      setDeleteData(feeToDelete);
      setModalType("inspector");
      setIsDeleteModalOpen(true);
    }
  };

  const confirmDeleteFee = () => {
    if (deleteData) {
      if (modalType === "client") {
        setClientFees((prev) => prev.filter((fee) => fee.id !== deleteData.id));
      } else {
        setInspectorFees((prev) =>
          prev.filter((fee) => fee.id !== deleteData.id)
        );
      }
      setIsDeleteModalOpen(false);
      setDeleteData(undefined);
    }
  };

  useEffect(() => {
    if (formik.values.fees && formik.values.fees.length === 0) {
      setClientFees([]);
      setInspectorFees([]);
    }
  }, [formik.values.fees, formik.values.notes]);

  return (
    <Box>
      <Typography variant="h5" className="section-label">
        Fees
      </Typography>

      <Box sx={{ mb: 4, mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Client</Typography>
          <Button
            variant="text"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openAddModal("client")}
            sx={{ textTransform: "none" }}
          >
            ADD CLIENT ADJUSTMENT
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "none" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Adjustment</TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Note</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedClientFees.map((fee, index) => (
                <TableRow key={fee.id}>
                  <TableCell>
                    {new Date(fee.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell>
                    {fee.type === "PreBillingAdjustment"
                      ? "Pre-Billing Adjustment"
                      : splitCamelCaseToWords(fee.type)}
                  </TableCell>
                  <TableCell>
                    {fee.adjustment && getAdjustmentDisplayName(fee.adjustment)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    ${parseFloat(fee.amount as any)?.toFixed(2) || "0.00"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>{fee.note}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        mr: 1,
                        visibility:
                          fee.type !== "Billing" ? "visible" : "hidden",
                      }}
                      onClick={() => handleDeleteClientRow(fee.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </Button>
                    <Button
                      size="small"
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        visibility:
                          fee.type !== "Billing" ? "visible" : "hidden",
                      }}
                      onClick={() => openEditModal("client", fee)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ borderBottom: "none" }}>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell
                  sx={{
                    textAlign: "right",
                    fontWeight: "bold",
                    borderBottom: "none",
                  }}
                >
                  ${clientSubtotal.toFixed(2)}
                </TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Inspector</Typography>
          <Button
            variant="text"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openAddModal("inspector")}
            sx={{ textTransform: "none" }}
          >
            ADD INSPECTOR ADJUSTMENT
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "none" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Adjustment</TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Note</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedInspectorFees.map((fee, index) => (
                <TableRow key={fee.id}>
                  <TableCell>
                    {new Date(fee.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell>
                    {fee.type === "PreBillingAdjustment"
                      ? "Pre-Billing Adjustment"
                      : splitCamelCaseToWords(fee.type)}
                  </TableCell>
                  <TableCell>
                    {fee.adjustment && getAdjustmentDisplayName(fee.adjustment)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    ${parseFloat(fee.amount as any)?.toFixed(2) || "0.00"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: "400" }}>{fee.note}</TableCell>
                  <TableCell sx={{ width: "180" }}>
                    <Button
                      size="small"
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        mr: 1,
                        visibility: fee.type !== "Cost" ? "visible" : "hidden",
                      }}
                      onClick={() => handleDeleteInspectorRow(fee.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </Button>
                    <Button
                      size="small"
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        visibility: fee.type !== "Cost" ? "visible" : "hidden",
                      }}
                      onClick={() => openEditModal("inspector", fee)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ borderBottom: "none" }}>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell
                  sx={{
                    textAlign: "right",
                    fontWeight: "bold",
                    borderBottom: "none",
                  }}
                >
                  ${inspectorSubtotal.toFixed(2)}
                </TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                <TableCell sx={{ borderBottom: "none" }}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <Box sx={{ display: "flex", gap: 3 }}>
          <Card
            sx={{
              flex: 1,
              border: "1px solid #C4C4C4",
              borderRadius: "8px",
              boxShadow: "none",
              height: "fit-content",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "600", fontSize: "16px" }}
              >
                Client
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Billed</Typography>
                <Typography>
                  $
                  {orderedClientFees.length > 0
                    ? toAmount(orderedClientFees[0].amount).toFixed(2)
                    : "0.00"}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Adjustments</Typography>
                <Typography>
                  $
                  {orderedClientFees.length > 1
                    ? orderedClientFees
                        .slice(1)
                        .reduce((total, fee) => total + toAmount(fee.amount), 0)
                        .toFixed(2)
                    : "0.00"}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  mt: 1,
                }}
              >
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  Adjusted Total
                </Typography>
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  ${clientSubtotal.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{
              flex: 1,
              border: "1px solid #C4C4C4",
              borderRadius: "8px",
              boxShadow: "none",
              height: "fit-content",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "600", fontSize: "16px" }}
              >
                Inspector
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Cost</Typography>
                <Typography>
                  $
                  {orderedInspectorFees.length > 0
                    ? toAmount(orderedInspectorFees[0].amount).toFixed(2)
                    : "0.00"}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Adjustments</Typography>
                <Typography>
                  $
                  {orderedInspectorFees.length > 1
                    ? orderedInspectorFees
                        .slice(1)
                        .reduce((total, fee) => total + toAmount(fee.amount), 0)
                        .toFixed(2)
                    : "0.00"}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  mt: 1,
                }}
              >
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  Adjusted Total
                </Typography>
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  ${inspectorSubtotal.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{
              flex: 1,
              border: "1px solid #C4C4C4",
              borderRadius: "8px",
              boxShadow: "none",
              height: "fit-content",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "600", fontSize: "16px" }}
              >
                Spectrum
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Margin</Typography>
                <Typography>
                  {orderedClientFees.length > 0 &&
                  orderedInspectorFees.length > 0
                    ? (
                        ((toAmount(orderedClientFees[0].amount) -
                          toAmount(orderedInspectorFees[0].amount)) /
                          toAmount(orderedClientFees[0].amount)) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  mt: 1,
                }}
              >
                <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                  Adjusted Margin
                </Typography>
                <Typography sx={{ fontWeight: "bold" }}>
                  {clientSubtotal > 0
                    ? (
                        ((clientSubtotal - inspectorSubtotal) /
                          clientSubtotal) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      <AdjustmentModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveAdjustment}
        onDelete={modalMode === "edit" ? handleDeleteAdjustment : undefined}
        mode={modalMode}
        adjustmentType={modalType}
        editData={editData}
        adjustmentOptions={adjustmentTypes || []}
      />

      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Delete Additional Information?</Typography>
            <IconButton
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteData(undefined);
              }}
            >
              <Typography>×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Are you sure you want to delete this{" "}
            {modalType === "client" ? "client" : "inspector"} fee?
            <br />
            This action can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => {
              setIsDeleteModalOpen(false);
              setDeleteData(undefined);
            }}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              textTransform: "none",
              "&:hover": { backgroundColor: "primary.main" },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={confirmDeleteFee}
            variant="text"
            sx={{
              color: "error.main",
              textTransform: "none",
            }}
          >
            DELETE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Fees;
